/**
 * What the signed-in staff member may do (T11-09, design/04 §14). The permission list is the one
 * identity-service put into the access token at sign-in (`perms` claim → `session.user`); the
 * UI only hides / disables — every service checks the same permission again (`x-permission`).
 *
 * `GET /v1/admin/auth/me` (design/07 §4.7) is not served by identity-service yet, so a role change
 * is picked up at the next token refresh / sign-in — see the T11-09 gaps.
 */
import { type Permission, ROLE_DEFAULTS, SYSTEM_ROLES, type SystemRole } from "@tcg/api-client";
import { useMemo } from "react";

import { type AccessRule, allows } from "@/auth/access";
import { useAuth } from "@/auth/use-auth";

export interface Permissions {
  permissions: readonly Permission[];
  roles: readonly string[];
  can(permission: Permission): boolean;
  /** At least one of them. */
  canAny(...permissions: Permission[]): boolean;
  canAll(...permissions: Permission[]): boolean;
  /** An access rule of a menu entry / route (`{permission}` / `{anyOf}`). */
  allows(rule: AccessRule | undefined): boolean;
}

export function permissionsOf(
  granted: readonly Permission[],
  roles: readonly string[] = [],
): Permissions {
  const set = new Set<Permission>(granted);
  const can = (permission: Permission) => set.has(permission);
  return {
    permissions: granted,
    roles,
    can,
    canAny: (...permissions) => permissions.some(can),
    canAll: (...permissions) => permissions.every(can),
    allows: (rule) => allows(rule, can),
  };
}

export function usePermissions(): Permissions {
  const { session } = useAuth();
  const granted = session?.user.permissions;
  const roles = session?.user.roles;
  return useMemo(() => permissionsOf(granted ?? [], roles ?? []), [granted, roles]);
}

/** System roles whose default set holds the permission (owner first). */
export function rolesWith(permission: Permission): SystemRole[] {
  return SYSTEM_ROLES.filter((role) => ROLE_DEFAULTS[role].includes(permission));
}

/**
 * Who to ask, in plain words, for a button this staff member cannot use:
 * `owner` = only the owner has it, `manager` = a manager (or the owner), `other` = ask the owner
 * to give the account the permission (custom roles).
 */
export type NeededRole = "owner" | "manager" | "other";

export function neededRole(permissions: readonly Permission[]): NeededRole {
  const holders = new Set(permissions.flatMap((p) => rolesWith(p)));
  if (holders.size === 1 && holders.has("owner")) {
    return "owner";
  }
  if (holders.has("manager")) {
    return "manager";
  }
  return "other";
}
